<template>
  <div class="week-swiper">
    <!-- Navigation and month label -->
    <div class="flex items-center justify-between mb-2">
      <div class="flex items-center">
        <o-button icon="chevronLeft" flat size="sm" @click="prevWeek" />
      </div>
      <div class="flex items-center space-x-2">
        <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
          {{ monthLabel }}
        </span>
        <o-button icon="calendar" flat size="sm" @click="goToToday" />
      </div>
      <div class="flex items-center">
        <o-button icon="chevronRight" flat size="sm" @click="nextWeek" />
      </div>
    </div>

    <!-- Week days -->
    <div class="flex gap-1">
      <div
        v-for="day in weekDaysWithData"
        :key="day.dateStr"
        class="flex-1 text-center cursor-pointer rounded-lg p-2 transition-colors"
        :class="[
          isSelected(day.dateStr)
            ? 'bg-primary-500 text-white'
            : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700',
          isToday(day.date) && !isSelected(day.dateStr) ? 'ring-2 ring-primary-300' : ''
        ]"
        @click="toggleDate(day.dateStr)"
      >
        <div class="text-xs opacity-70">{{ day.dayName }}</div>
        <div class="text-lg font-medium">{{ day.dayNum }}</div>
        <div v-if="day.hasData" class="w-2 h-2 rounded-full mx-auto mt-1" :class="isSelected(day.dateStr) ? 'bg-white' : 'bg-primary-500'" />
        <div v-else class="h-2 mt-1" />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    modelValue: {
      type: Array,
      default: undefined
    },
    value: {
      type: Array,
      default: () => []
    },
    multiple: {
      type: Boolean,
      default: false
    },
    dataDates: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      weekStart: null
    }
  },
  computed: {
    selectedValue () {
      return this.modelValue ?? this.value
    },
    weekDays () {
      if (!this.weekStart) { return [] }

      const days = []
      let current = new Date(this.weekStart)

      for (let i = 0; i < 7; i++) {
        days.push({
          date: new Date(current),
          dateStr: this.$date.format(current, 'yyyyMMdd'),
          dayName: this.$date.format(current, 'EEE'),
          dayNum: current.getDate()
        })
        current = this.$date.add(current, { days: 1 })
      }

      return days
    },
    monthLabel () {
      if (!this.weekStart) { return '' }

      const start = this.weekDays[0]?.date
      const end = this.weekDays[6]?.date

      if (!start || !end) { return '' }

      const startMonth = this.$date.format(start, 'MMM yyyy')
      const endMonth = this.$date.format(end, 'MMM yyyy')

      if (startMonth === endMonth) {
        return startMonth
      }

      return `${this.$date.format(start, 'MMM')} - ${endMonth}`
    },
    weekDates () {
      return this.weekDays.map(day => day.dateStr)
    },
    dataDatesSet () {
      return new Set(this.dataDates)
    },
    weekDaysWithData () {
      const dataSet = this.dataDatesSet
      return this.weekDays.map(day => ({
        ...day,
        hasData: dataSet.has(day.dateStr)
      }))
    }
  },
  watch: {
    weekDates: {
      handler (dates) {
        this.$emit('week-change', dates)
      },
      immediate: true
    }
  },
  created () {
    this.initWeekStart()
  },
  methods: {
    initWeekStart () {
      const today = new Date()
      const configuredFirstDay = Number(this.$auth?.info?.location?.startDayOfWeek)
      const firstDayOfWeek = Number.isInteger(configuredFirstDay) && configuredFirstDay >= 1 && configuredFirstDay <= 7
        ? configuredFirstDay
        : 1
      const dayOfWeek = today.getDay()
      const diff = (dayOfWeek - firstDayOfWeek + 7) % 7
      this.weekStart = this.$date.add(today, { days: -diff })
    },
    prevWeek () {
      this.weekStart = this.$date.add(this.weekStart, { days: -7 })
    },
    nextWeek () {
      this.weekStart = this.$date.add(this.weekStart, { days: 7 })
    },
    goToToday () {
      this.initWeekStart()
    },
    isSelected (dateStr) {
      return this.selectedValue.includes(dateStr)
    },
    isToday (date) {
      const today = new Date()
      return date.toDateString() === today.toDateString()
    },
    hasData (dateStr) {
      return this.dataDatesSet.has(dateStr)
    },
    toggleDate (dateStr) {
      if (this.multiple) {
        const newValue = [...this.selectedValue]
        const index = newValue.indexOf(dateStr)

        if (index === -1) {
          newValue.push(dateStr)
        } else {
          newValue.splice(index, 1)
        }

        this.$emit('update:modelValue', newValue)
        this.$emit('input', newValue)
      } else {
        // Single selection mode
        if (this.selectedValue.includes(dateStr)) {
          this.$emit('update:modelValue', [])
          this.$emit('input', [])
        } else {
          this.$emit('update:modelValue', [dateStr])
          this.$emit('input', [dateStr])
        }
      }
    }
  }
}
</script>
